import type {Optional} from '@yandex-delivery/types'
import {flow, get, pipe, toDefaultZero, toGrams, toNumber} from '@yandex-delivery/utils'

type GetFunction = (size?: ParcelDimensions) => number

export const getLength: GetFunction = flow(get('length'), toNumber)
export const getHeight: GetFunction = flow(get('height'), toNumber)
export const getWidth: GetFunction = flow(get('width'), toNumber)
export const toWeight: (weight?: number) => number = flow(toNumber, toGrams, Math.round)
export const getWeightGross: GetFunction = flow(get('weight'), toWeight)

export type ParcelSize = 'small' | 'medium' | 'large' | 'custom'

export const enum ParcelPaymentType {
  Payed = 'payed',
  OnDemand = 'onDemand',
}

export type ParcelDimensions = {
  height: number
  length: number
  weight: number
  width: number
}

export const getParcelHeight = (
  customSizes: CustomParcelSizes,
  dimensions: Optional<ParcelDimensions>,
  size: Optional<ParcelSize>,
) => {
  const dimensionHeight = getHeight(dimensions)

  if (dimensionHeight === 0) {
    const fallbackValue = pipe(customSizes, get('small.height'), toDefaultZero)
    const dimensionHeightFromCustomSizes = pipe(customSizes, get(`${size}.height`), toDefaultZero, toNumber)

    return dimensionHeightFromCustomSizes || fallbackValue
  }

  return toNumber(dimensionHeight)
}

export const getParcelLength = (
  customSizes: CustomParcelSizes,
  dimensions: Optional<ParcelDimensions>,
  size: Optional<ParcelSize>,
): number => {
  const dimensionLength = getLength(dimensions)

  if (dimensionLength === 0) {
    const fallbackValue = pipe(customSizes, get('small.length'), toDefaultZero)
    const dimensionLengthFromCustomSizes = pipe(customSizes, get(`${size}.length`), toDefaultZero, toNumber)

    return dimensionLengthFromCustomSizes || fallbackValue
  }

  return dimensionLength
}

export const getParcelWeight = (
  customSizes: CustomParcelSizes,
  dimensions: Optional<ParcelDimensions>,
  size: Optional<ParcelSize>,
) => {
  const dimensionWeight = getWeightGross(dimensions)

  if (dimensionWeight === 0) {
    const fallbackValue = pipe(customSizes, get('small.weight'), toDefaultZero)
    const dimensionWeightFromCustomSizes = pipe(customSizes, get(`${size}.weight`), toDefaultZero, toNumber)
    const weight = dimensionWeightFromCustomSizes || fallbackValue

    return toWeight(weight)
  }

  return dimensionWeight
}

export const getParcelWidth = (
  customSizes: CustomParcelSizes,
  dimensions: Optional<ParcelDimensions>,
  size: Optional<ParcelSize>,
): number => {
  const dimensionWidth = getWidth(dimensions)

  if (dimensionWidth === 0) {
    const fallbackValue = pipe(customSizes, get('small.width'), toDefaultZero)
    const dimensionWidthFromCustomSizes = pipe(customSizes, get(`${size}.width`), toDefaultZero, toNumber)

    return dimensionWidthFromCustomSizes || fallbackValue
  }

  return dimensionWidth
}
